/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import '@asseinfo/react-kanban/dist/styles.css';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';
import MDDatePicker from 'components/MDDatePicker';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  code: '',
  name: '',
  promotion_name: '',
  barcode_id: '',
  campaign_id: '',
  created_at: '',
  quantity: '',
  price: '',
  amount: '',
  date_start: Func.dateNow(),
  time_start: '00:00',
  date_stop: Func.dateAdd(365),
  time_stop: '00:00',
};

function ProductPromotion({ parent, reload }) {
  const mod = 'product_promotion';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [notify, setNotify] = useState(null);
  const [editCampaign, setEditCampaign] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const refreshCode = () => {
    setAttr({ ...attr, code: parent.barcode });
  };

  const refreshName = () => {
    const amount = attr.amount * 1;
    const txt = `${parent.name} [${attr.quantity}+ =${amount}]`;
    setAttr({ ...attr, name: txt });
  };

  const refreshPromotion = () => {
    setAttr({ ...attr, promotion_name: parent.name });
  };

  const changeQuantity = (input) => {
    const price = attr.amount * 1;
    const quantity = input * 1;
    const amount = Math.round(quantity === 0 ? 0 : price / quantity, 2);
    const name = `${parent.name} [${attr.quantity}+ =${price}]`;

    setAttr({ ...attr, name: name, amount: `${amount}`, quantity: `${quantity}` });
  };

  const changePrice = (input) => {
    const price = input * 1;
    const quantity = attr.quantity * 1;
    const amount = Math.round(quantity === 0 ? 0 : price / quantity, 2);
    const name = `${parent.name} [${attr.quantity}+ =${price}]`;

    setAttr({ ...attr, name: name, price: `${price}`, amount: `${amount}` });
  };

  const view = () => {
    setLoading(true);
    FetchData('POST', `/${mod}`, app.token, {
      id: parent.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setList(result.list);
        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.code, 1)) return;
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setConfirm(false);
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      barcode_id: parent.id,
      code: attr.code,
      name: attr.name,
      promotion_name: attr.promotion_name,
      quantity: attr.quantity,
      amount: attr.amount,
      price: attr.price,
      date_start: attr.date_start,
      time_start: attr.time_start,
      date_stop: attr.date_stop,
      time_stop: attr.time_stop,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setAttr(initAttr);
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const swap = (data) => {
    FetchData('OPTIONS', `/${mod}/swap`, app.token, {
      id: parent.id,
      list: data,
    }).then((result) => {
      if (result.status === 'ok') {
        showNotify(t(`alert.savecomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(list);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setList(items);
    swap(items);
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const open = () => {
    setAttr(initAttr);
    setModalOpen(true);
    setEditCampaign(false);

    view();
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderButton = () => {
    return (
      <MDBox mt={2}>
        {confirm ? (
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='outlined' color='success' onClick={add}>
                <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
              </MDButton>
            </Grid>
          </Grid>
        ) : (
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='outlined' color='error' onClick={() => setAttr(initAttr)}>
                <Icon>cancel</Icon>&nbsp;{t(`element.clear`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='outlined' color='info' onClick={addConfirm}>
                <Icon>save</Icon>&nbsp;{t(`element.save`)}
              </MDButton>
            </Grid>
          </Grid>
        )}
      </MDBox>
    );
  };
  const renderInfo = () => {
    return (
      <MDBox className='card' p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='รหัส'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, code: e.target.value })}
              value={attr.code}
              variant='standard'
              type='text'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDButton iconOnly variant='text' color='info' onClick={refreshCode}>
                      <Icon>refresh</Icon>
                    </MDButton>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='แคมเปญ'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, name: e.target.value })}
              value={attr.name}
              variant='standard'
              type='text'
              fullWidth
              multiline
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDButton iconOnly variant='text' color='info' onClick={refreshName}>
                      <Icon>refresh</Icon>
                    </MDButton>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='Promotion'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, promotion_name: e.target.value })}
              value={attr.promotion_name}
              variant='standard'
              type='text'
              fullWidth
              multiline
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDButton iconOnly variant='text' color='info' onClick={refreshPromotion}>
                      <Icon>refresh</Icon>
                    </MDButton>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MDInput
              label='จำนวน'
              onFocus={(e) => e.target.select()}
              onChange={(e) => changeQuantity(e.target.value)}
              value={attr.quantity}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>{parent.unit_name}</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MDInput
              label='ราคาลดเหลือ'
              onFocus={(e) => e.target.select()}
              onChange={(e) => changePrice(e.target.value)}
              value={attr.price}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`ราคา / ${parent.unit_name}`}
              value={attr.amount}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  readOnly: true,
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={7}>
            <MDDatePicker
              onChange={([date]) => {
                setAttr({ ...attr, date_start: Func.dateFormat(date) });
              }}
              value={new Date(attr.date_start)}
              input={{
                label: 'เริ่มต้น',
                variant: 'standard',
                fullWidth: true,
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <MDInput
              label='เวลา'
              onChange={(e) => setAttr({ ...attr, time_start: e.target.value })}
              value={attr.time_start}
              variant='standard'
              fullWidth
              type='time'
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={7}>
            <MDDatePicker
              onChange={([date]) => {
                setAttr({ ...attr, date_stop: Func.dateFormat(date) });
              }}
              value={new Date(attr.date_stop)}
              input={{
                label: 'สิ้นสุด',
                variant: 'standard',
                fullWidth: true,
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <MDInput
              label='เวลา'
              onChange={(e) => setAttr({ ...attr, time_stop: e.target.value })}
              value={attr.time_stop}
              variant='standard'
              fullWidth
              type='time'
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
        {renderButton()}
      </MDBox>
    );
  };

  const btnEdit = (item) => {
    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton
          variant='text'
          color='success'
          size='small'
          iconOnly
          onClick={() => {
            setAttr(item);
          }}
        >
          <Icon>border_color</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnClear = (item) => {
    return (
      <Tooltip title={t(`element.clear`)}>
        <MDButton
          variant='text'
          color='error'
          size='small'
          iconOnly
          onClick={() => setAttr(initAttr)}
        >
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const dateStartStop = (item) => {
    if (item.check_date === 'N')
      return (
        <span className='color-danger'>
          {Func.dateDisplay(item.date_start)} - {Func.dateDisplay(item.date_stop)}
        </span>
      );

    return (
      <span>
        {Func.dateDisplay(item.date_start)} - {Func.dateDisplay(item.date_stop)}
      </span>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId='characters'>
            {(provided) => (
              <Grid container {...provided.droppableProps} ref={provided.innerRef} spacing={1}>
                {list.map((item, index) => {
                  return (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided) => (
                        <Grid
                          item
                          xs={12}
                          xl={12}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <MDBox className='card' p={1}>
                            <MDBox
                              sx={{
                                display: 'flex',
                                justifyContent: 'left',
                                width: '100%',
                                gap: 1,
                                fontSize: 15,
                                fontWeight: 400,
                                textTransform: 'none',
                              }}
                            >
                              <div>{Func.imageDisplay(item)}</div>
                              <div>
                                <h4>
                                  {item.code} : {item.name}
                                </h4>
                                <p>
                                  ซื้อครบ {Func.numberDisplay(item.quantity)} {item.unit_name}{' '}
                                  ราคาลดเหลือ {Func.numberDisplay(item.price)} บาท ชิ้นถัดไปเหลือ{' '}
                                  {Func.numberDisplay(item.amount)} บาท
                                  {btnDel(item)}
                                </p>
                              </div>
                            </MDBox>
                          </MDBox>
                        </Grid>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>
      </MDBox>
    );
  };

  const btnOpen = () => {
    return (
      <Tooltip title='Promotion'>
        <IconButton aria-label='Promotion' onClick={open} color='primary'>
          <Icon>redeem</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => {
          setModalOpen(false);
          if (editCampaign) reload();
        }}
      >
        <DialogTitle>
          <MDBox className='cut' sx={{ width: 600 }}>
            {parent.barcode} : {parent.name}
          </MDBox>
          <IconButton
            aria-label='close'
            onClick={() => {
              setModalOpen(false);
              if (editCampaign) reload();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5} lg={4}>
              {renderInfo()}
            </Grid>
            <Grid item xs={12} md={7} lg={8}>
              {renderList()}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}
      {renderLoading()}
      {notify}
    </div>
  );
}

ProductPromotion.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    barcode: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    unit_name: PropTypes.string.isRequired,
  }).isRequired,
  reload: PropTypes.func.isRequired,
};

export default ProductPromotion;
